import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
} from 'chart.js';
import Sparkline from './Sparkline';
import TradingAutomation from './TradingAutomation';
import { useI18n } from '../i18n';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const RANGE_OPTIONS = {
  '1W': { label: '1W', period: '7d', interval: '30m' },
  '1M': { label: '1M', period: '1mo', interval: '1d' },
  '3M': { label: '3M', period: '3mo', interval: '1d' },
  '1Y': { label: '1Y', period: '1y', interval: '1d' },
  '5Y': { label: '5Y', period: '5y', interval: '1d' }
};

const FALLBACK_SYMBOLS = ['AAPL', 'MSFT', 'NVDA', 'AMZN', 'TSLA', 'META', 'JPM', 'V'];

const API_BASE = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';

function formatCurrency(v, currency = 'USD') {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 });
  return formatter.format(Number(v));
}

function formatPercent(v, digits = 2) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const pct = Number(v);
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(digits)}%`;
}

function formatNumber(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const num = Number(v);
  if (Math.abs(num) >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toFixed(0);
}

function buildSparkline(basePrice = 0) {
  const start = Number(basePrice) || 100;
  return Array.from({ length: 18 }, (_, idx) => start + Math.sin(idx / 2.5) * 1.6 + (Math.random() - 0.5) * 0.9);
}

export default function TradingTerminal({ market = 'US', onMarketChange = () => {} }) {
  const { t, lang } = useI18n();
  const [watchlist, setWatchlist] = useState([]);
  const [watchLoading, setWatchLoading] = useState(false);
  const [focusSymbol, setFocusSymbol] = useState('AAPL');
  const [rangeKey, setRangeKey] = useState('1M');
  const [focusDetails, setFocusDetails] = useState(null);
  const [focusIndicators, setFocusIndicators] = useState(null);
  const [focusHistory, setFocusHistory] = useState([]);
  const [focusPrediction, setFocusPrediction] = useState(null);
  const [insights, setInsights] = useState([]);
  const [financialAnalysis, setFinancialAnalysis] = useState(null);
  const [aiState, setAiState] = useState('idle');
  const [latencyMs, setLatencyMs] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadWatchlist() {
      setWatchLoading(true);
      try {
        // Try managed watchlist first
        const managedRes = await axios.get(`${API_BASE}/api/watchlist?market=${market}&limit=10`);
        
        if (managedRes.data?.source === 'managed' && managedRes.data.watchlist?.length > 0) {
          // Use managed watchlist
          const enriched = managedRes.data.watchlist.map((item, idx) => ({
            symbol: item.symbol || FALLBACK_SYMBOLS[idx % FALLBACK_SYMBOLS.length],
            price: item.price ?? 0,
            change: item.change_percent ?? 0,
            market: item.market || market,
            name: item.name || item.symbol,
            volume: item.volume,
            spark: buildSparkline(item.price)
          }));
          
          if (mounted) {
            setWatchlist(enriched);
            if (!focusSymbol && enriched.length) {
              setFocusSymbol(enriched[0].symbol);
            }
          }
        } else {
          // Fallback to auto-populated by volume
          const res = await axios.get(`${API_BASE}/api/market/${market}?limit=20&sort_by=volume&sort_order=desc&fetch_prices=false`);
          const rows = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
          const enriched = rows.map((row, idx) => ({
            symbol: row.symbol || FALLBACK_SYMBOLS[idx % FALLBACK_SYMBOLS.length],
            price: row.price ?? row.last_price ?? 0,
            change: row.change_percent ?? row.change ?? 0,
            market: row.market || market,
            name: row.name || row.company || row.symbol,
            volume: row.volume,
            spark: buildSparkline(row.price)
          }));
          
          if (mounted) {
            setWatchlist(enriched.slice(0, 10));
            if (!focusSymbol && enriched.length) {
              setFocusSymbol(enriched[0].symbol);
            }
          }
        }
      } catch (err) {
        console.error('Watchlist load error:', err);
        // On error, try fallback endpoint
        try {
          const res = await axios.get(`${API_BASE}/api/market/${market}?limit=20&sort_by=volume&sort_order=desc&fetch_prices=false`);
          const rows = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
          const enriched = rows.map((row, idx) => ({
            symbol: row.symbol || FALLBACK_SYMBOLS[idx % FALLBACK_SYMBOLS.length],
            price: row.price ?? row.last_price ?? 0,
            change: row.change_percent ?? row.change ?? 0,
            market: row.market || market,
            name: row.name || row.company || row.symbol,
            volume: row.volume,
            spark: buildSparkline(row.price)
          }));
          
          if (mounted) {
            setWatchlist(enriched.slice(0, 10));
            if (!focusSymbol && enriched.length) {
              setFocusSymbol(enriched[0].symbol);
            }
          }
        } catch (finalErr) {
          console.error('Final watchlist error:', finalErr);
        }
      } finally {
        if (mounted) setWatchLoading(false);
      }
    }
    loadWatchlist();
    const interval = setInterval(loadWatchlist, 60000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [market]);

  useEffect(() => {
    if (!focusSymbol) return;
    let active = true;
    async function loadFocus() {
      // Clear old insights and news immediately when symbol changes
      setInsights([]);
      setNews([]);
      setFinancialAnalysis(null);
      setAiState('loading');
      setNewsLoading(true);
      setError(null);
      const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const cfg = RANGE_OPTIONS[rangeKey] || RANGE_OPTIONS['1M'];
      try {
        const [detailsRes, historyRes, analysisRes, financialRes, predictionRes, insightsRes, newsRes] = await Promise.allSettled([
          axios.get(`${API_BASE}/api/yahoo/details/${focusSymbol}`),
          axios.get(`${API_BASE}/api/yahoo/history/${focusSymbol}?period=${cfg.period}&interval=${cfg.interval}`),
          axios.get(`${API_BASE}/api/analysis/${focusSymbol}`),
          axios.get(`${API_BASE}/api/financial/analysis/${focusSymbol}`),
          axios.get(`${API_BASE}/api/predict/${focusSymbol}?days_ahead=3&period=${cfg.period}`),
          axios.get(`${API_BASE}/api/insights/${focusSymbol}?period=${cfg.period}${lang === 'vi' ? '&translate=vi' : ''}`),
          axios.get(`${API_BASE}/api/news/${focusSymbol}?limit=5${lang === 'vi' ? '&translate=vi' : ''}`)
        ]);

        if (!active) return;

        if (detailsRes.status === 'fulfilled') {
          setFocusDetails(detailsRes.value.data);
        }
        if (analysisRes.status === 'fulfilled') {
          setFocusIndicators(analysisRes.value.data);
        }
        if (historyRes.status === 'fulfilled') {
          const candles = historyRes.value.data?.history || [];
          setFocusHistory(candles);
        }
        if (financialRes.status === 'fulfilled') {
          const faData = financialRes.value.data;
          if (faData && faData.status !== 'disabled') {
            setFinancialAnalysis(faData);
          }
        }
        if (predictionRes.status === 'fulfilled') {
          setFocusPrediction(predictionRes.value.data);
        }
        if (insightsRes.status === 'fulfilled') {
          const raw = insightsRes.value.data;
          const list = Array.isArray(raw?.insights) ? raw.insights : raw?.insights ? [raw.insights] : [];
          setInsights(list.map((entry, idx) => ({ id: idx, text: String(entry) })));
        }
        if (newsRes.status === 'fulfilled') {
          const newsData = newsRes.value.data?.news || [];
          setNews(newsData);
        }
        setAiState('ready');
        setNewsLoading(false);
        setLastUpdated(new Date());
        const end = typeof performance !== 'undefined' ? performance.now() : Date.now();
        setLatencyMs(Math.round(end - start));
      } catch (e) {
        console.error('focus load failed', e);
        if (!active) return;
        setError('Unable to synchronize AI modules for this symbol.');
        setAiState('error');
      }
    }
    loadFocus();
    return () => {
      active = false;
    };
  }, [focusSymbol, rangeKey, lang]);

  const chartData = useMemo(() => {
    if (!focusHistory || !focusHistory.length) return null;
    // Determine chart color based on price change
    const isPositive = focusDetails?.change_percent >= 0;
    const borderColor = isPositive ? '#34d399' : '#f87171';
    const backgroundColor = isPositive ? 'rgba(52, 211, 153, 0.12)' : 'rgba(248, 113, 113, 0.12)';
    
    return {
      labels: focusHistory.map((row) => new Date(row.timestamp).toLocaleDateString()),
      datasets: [
        {
          label: focusSymbol,
          data: focusHistory.map((row) => Number(row.close)),
          borderColor: borderColor,
          backgroundColor: backgroundColor,
          pointRadius: 0,
          fill: true,
          tension: 0.32
        }
      ]
    };
  }, [focusHistory, focusSymbol, focusDetails?.change_percent]);

  const aiPlaybook = useMemo(() => {
    const ideas = [];
    if (focusIndicators?.rsi != null) {
      ideas.push({
        label: focusIndicators.rsi > 70 ? 'Overbought fade' : 'Accumulation window',
        confidence: focusIndicators.rsi > 70 ? 0.52 : 0.68,
        detail: `RSI ${Number(focusIndicators.rsi).toFixed(1)}`
      });
    }
    if (focusIndicators?.macd && focusIndicators?.macd_signal) {
      const diff = Number(focusIndicators.macd) - Number(focusIndicators.macd_signal);
      ideas.push({
        label: diff >= 0 ? 'Momentum ignition' : 'Mean reversion',
        confidence: Math.min(0.9, Math.max(0.45, Math.abs(diff) * 5)),
        detail: `MACD Δ ${diff.toFixed(3)}`
      });
    }
    if (focusPrediction?.expected_move) {
      ideas.push({
        label: 'Projected move',
        confidence: Math.min(0.95, Math.abs(focusPrediction.expected_move) / 5),
        detail: formatPercent(focusPrediction.expected_move)
      });
    }
    if (!ideas.length) {
      ideas.push({ label: 'Data sync pending', confidence: 0.32, detail: 'Waiting for indicators' });
    }
    return ideas.slice(0, 3);
  }, [focusIndicators, focusPrediction, focusSymbol]);

  const matrixRows = useMemo(() => {
    const source = [
      { name: 'EMA Pullback', metric: focusIndicators?.ema_12 && focusIndicators?.ema_26 ? (focusIndicators.ema_12 - focusIndicators.ema_26) : null },
      { name: 'VWAP Bounce', metric: focusIndicators?.vwap || null },
      { name: 'Breakout Grid', metric: focusIndicators?.atr || null },
      { name: 'Gap Momentum', metric: focusPrediction?.expected_move || null }
    ];
    return source.map((row) => {
      if (row.metric == null) {
        return { ...row, bias: 'neutral', status: 'Awaiting data' };
      }
      let bias = 'neutral';
      let status = 'Range';
      if (row.metric > 0) {
        bias = 'bull';
        status = 'Long bias';
      } else if (row.metric < 0) {
        bias = 'bear';
        status = 'Short bias';
      }
      return { ...row, bias, status };
    });
  }, [focusIndicators, focusPrediction]);

  const sessionStats = useMemo(() => {
    return [
      { label: 'Latency', value: latencyMs ? `${latencyMs}ms` : 'syncing', intent: 'neutral' },
      { label: 'Last sync', value: lastUpdated ? lastUpdated.toLocaleTimeString() : '—', intent: 'neutral' },
      { label: 'AI State', value: aiState === 'ready' ? 'Aligned' : aiState, intent: aiState === 'ready' ? 'positive' : aiState === 'error' ? 'negative' : 'neutral' }
    ];
  }, [latencyMs, lastUpdated, aiState]);

  return (
    <section className="terminal">
      <div className="terminal-grid">
        <article className="panel watchlist-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">LIVE WATCHLIST</p>
              <h3>{t('stocklist.title')}</h3>
            </div>
          </div>
          {watchLoading ? <div className="loading">{t('loading')}</div> : null}
          <div className="watchlist-items">
            {watchlist.map((item) => (
              <button
                key={item.symbol}
                className={`watchlist-item ${focusSymbol === item.symbol ? 'selected' : ''}`}
                onClick={() => setFocusSymbol(item.symbol)}
              >
                <div>
                  <div className="symbol-row">
                    <span className="symbol-tag">{item.symbol}</span>
                    <span className={`badge-chip ${item.change >= 0 ? 'up' : 'down'}`}>{formatPercent(item.change)}</span>
                  </div>
                  <div className="watch-price">{formatCurrency(item.price)}</div>
                  <div className="watch-meta">Vol {formatNumber(item.volume)}</div>
                </div>
                <Sparkline data={item.spark} color={item.change >= 0 ? '#34d399' : '#f87171'} width={80} height={32} />
              </button>
            ))}
            {!watchlist.length && !watchLoading ? (
              <div className="empty">Configure watchlist in Admin Dashboard or populate cache to auto-generate.</div>
            ) : null}
          </div>
        </article>

        <article className="panel focus-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">FOCUS SYMBOL</p>
              <h3>{focusSymbol}</h3>
            </div>
            <div className="range-toggle">
              {Object.keys(RANGE_OPTIONS).map((key) => (
                <button
                  key={key}
                  className={rangeKey === key ? 'range-pill active' : 'range-pill'}
                  onClick={() => setRangeKey(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
          <div className="focus-header-compact">
            <div className="header-col">
              <span className="label">Current Price</span>
              <span className="value">{focusDetails ? formatCurrency(focusDetails.price, focusDetails.currency || 'USD') : '—'}</span>
            </div>
            <div className="header-col">
              <span className="label">Close</span>
              <span className="value">{focusDetails?.close ? formatCurrency(focusDetails.close, focusDetails.currency || 'USD') : '—'}</span>
            </div>
            <div className="header-col">
              <span className="label">Change</span>
              <span className={`value ${focusDetails?.change_percent >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(focusDetails?.change_percent)}
              </span>
            </div>
          </div>
          <div className="chart-frame">
            {chartData ? <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } }, scales: { x: { display: true, ticks: { maxTicksLimit: 5 } }, y: { display: true, position: 'right' } } }} /> : <div className="loading">{t('chart.loading')}</div>}
          </div>
          <div className="chart-metrics">
            <div className="metrics-row">
              <div className="metric-item">
                <span className="label">Open</span>
                <span className="value">{focusDetails?.open ? formatCurrency(focusDetails.open, focusDetails.currency || 'USD') : '—'}</span>
              </div>
              <div className="metric-item">
                <span className="label">High</span>
                <span className="value">{focusDetails?.dayHigh ? formatCurrency(focusDetails.dayHigh, focusDetails.currency || 'USD') : '—'}</span>
              </div>
              <div className="metric-item">
                <span className="label">Low</span>
                <span className="value">{focusDetails?.dayLow ? formatCurrency(focusDetails.dayLow, focusDetails.currency || 'USD') : '—'}</span>
              </div>
              <div className="metric-item">
                <span className="label">Volume</span>
                <span className="value">{focusDetails?.volume ? formatNumber(focusDetails.volume) : focusDetails?.avgVolume ? formatNumber(focusDetails.avgVolume) : '—'}</span>
              </div>
            </div>
            <div className="metrics-row">
              <div className="metric-item">
                <span className="label">52W High</span>
                <span className="value">{focusDetails?.high52 ? formatCurrency(focusDetails.high52, focusDetails.currency || 'USD') : '—'}</span>
              </div>
              <div className="metric-item">
                <span className="label">52W Low</span>
                <span className="value">{focusDetails?.low52 ? formatCurrency(focusDetails.low52, focusDetails.currency || 'USD') : '—'}</span>
              </div>
              <div className="metric-item">
                <span className="label">P/E</span>
                <span className="value">{focusDetails?.peRatio ? focusDetails.peRatio.toFixed(2) : '—'}</span>
              </div>
              <div className="metric-item">
                <span className="label">Market Cap</span>
                <span className="value">{focusDetails?.marketCap ? formatNumber(focusDetails.marketCap) : '—'}</span>
              </div>
            </div>
          </div>
          <div className="playbook">
            {aiPlaybook.map((idea, idx) => (
              <div key={idx} className="ai-chip">
                <div>
                  <p>{idea.label}</p>
                  <small>{idea.detail}</small>
                </div>
                <strong>{(idea.confidence * 100).toFixed(0)}%</strong>
              </div>
            ))}
          </div>
          {error ? <div className="inline-error">{error}</div> : null}
          <div className="session-stats">
            {sessionStats.map((stat) => (
              <div key={stat.label} className={`stat ${stat.intent}`}>
                <p>{stat.label}</p>
                <strong>{stat.value}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="panel ai-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">AI COMMAND CENTER</p>
              <h3>{focusSymbol} Analysis</h3>
            </div>
            <span className={`status-chip ${aiState}`}>{aiState === 'ready' ? 'ALIGNED' : aiState.toUpperCase()}</span>
          </div>
          
          {/* Financial Analysis Section */}
          {financialAnalysis && financialAnalysis.status === 'success' && (
            <div className="financial-analysis-block">
              <h4>Financial Assessment (BCTT)</h4>
              {financialAnalysis.score && (
                <div className="fa-score">
                  <span className="score-label">Overall Health</span>
                  <span className={`score-value ${financialAnalysis.score >= 7 ? 'positive' : financialAnalysis.score >= 5 ? 'neutral' : 'negative'}`}>
                    {Number(financialAnalysis.score).toFixed(1)}/10
                  </span>
                </div>
              )}
              
              {financialAnalysis.sentiment && (
                <div className="fa-sentiment">
                  <span className={`sentiment-badge ${financialAnalysis.sentiment.toLowerCase()}`}>
                    {financialAnalysis.sentiment}
                  </span>
                </div>
              )}
              
              {financialAnalysis.strengths && Array.isArray(financialAnalysis.strengths) && (
                <div className="fa-section">
                  <p className="section-label">✓ Strengths</p>
                  <ul>
                    {financialAnalysis.strengths.slice(0, 3).map((s, idx) => (
                      <li key={idx}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {financialAnalysis.concerns && Array.isArray(financialAnalysis.concerns) && (
                <div className="fa-section">
                  <p className="section-label">⚠ Concerns</p>
                  <ul>
                    {financialAnalysis.concerns.slice(0, 3).map((c, idx) => (
                      <li key={idx}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {financialAnalysis.summary && (
                <div className="fa-summary">
                  <p>{financialAnalysis.summary}</p>
                </div>
              )}
            </div>
          )}
          
          {financialAnalysis && financialAnalysis.status === 'scheduled' && (
            <div className="financial-analysis-block">
              <p className="status-msg">Financial analysis scheduled... check back soon</p>
            </div>
          )}
          
          <div className="insights">
            {insights.length ? insights.slice(0, 6).map((entry) => (
              <div key={entry.id} className="insight-card">
                <span>{entry.text}</span>
              </div>
            )) : <div className="loading">{t('loading')}</div>}
          </div>
        </article>

        {/* Trading Automation - Temporarily disabled
        <article className="panel automation-panel">
          <TradingAutomation 
            symbol={focusSymbol} 
            currentPrice={focusDetails?.currentPrice || 0}
          />
        </article>
        */}

        <article className="panel news-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">LATEST NEWS</p>
              <h3>{focusSymbol} Updates</h3>
            </div>
            {newsLoading && <span className="status-chip loading">LOADING</span>}
          </div>
          <div className="news-list">
            {news.length ? news.map((article, idx) => (
              <a 
                key={idx} 
                href={article.link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="news-item"
              >
                <div className="news-content">
                  <h4 className="news-title">{article.title_translated || article.title}</h4>
                  <div className="news-meta">
                    <span className="news-publisher">{article.publisher}</span>
                    {article.published && (
                      <>
                        <span className="news-separator">•</span>
                        <span className="news-date">
                          {new Date(article.published).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <span className="news-arrow">→</span>
              </a>
            )) : !newsLoading ? (
              <div className="empty">No recent news available for {focusSymbol}</div>
            ) : (
              <div className="loading">{t('loading')}</div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
}
